import { 
  Box, 
  Checkbox,
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControlLabel,
  FormHelperText, 
  IconButton, 
  OutlinedInput, 
  Switch,
  Typography,
  FormGroup,
  Divider
} from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { 
  createTermsAndConditionsAction, 
  updateTermsAndConditionsAction 
} from "../../Actions/termsAndConditionsAction";
import { SelectDropdown_SX } from "../../Assets/customTheme/constants";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import TipTapEditorComponent from "../../components/Common/TipTapEditorComponent";
import PreviewTemplateModel from "./TermsAndConditionPreviewModal";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

const AddEditTermsConditionModal = ({ 
  open, 
  handleClose, 
  templateDataById, 
  setSnackbar, 
  getTemplateData 
}) => {
  const [loading, setLoading] = useState(false);
  const [openPreviewModel, setOpenPreviewModel] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const handleCloseModal = () => {
    setOpenPreviewModel(false);
  };

  const handlePreviewModel = () => {
    setOpenPreviewModel(true);
  };

  const validationSchema = yup.object({
    templateTitle: yup
      .string()
      .trim()
      .min(3, VALIDATION.MIN_LENGTH("Title", 3))
      .max(100, VALIDATION.MAX_LENGTH("Title", 100))
      .matches(/^[a-zA-Z0-9\s_\-]+$/, VALIDATION.INVALID_FORMAT("Title"))
      .required(VALIDATION.REQUIRED_FIELD("Title")),
    templateContent: yup
      .string()
      .trim()
      .required(VALIDATION.REQUIRED_FIELD("Template content")),
    isMaster: yup.boolean(),
    isTenant: yup.boolean(),
    isActive: yup.boolean(),
  }).test(
    'at-least-one-type',
    VALIDATION.AT_LEAST_ONE_REQUIRED(["Master", "Tenant"]),
    function (values) {
      const { isMaster, isTenant } = values;
      if (!isMaster && !isTenant) {
        return this.createError({
          path: 'isMaster',
          message: VALIDATION.AT_LEAST_ONE_REQUIRED(["Master", "Tenant"]),
        });
      }
      return true;
    }
  );

  const formik = useFormik({
    initialValues: {
      templateTitle: templateDataById?.title || "",
      templateContent: templateDataById?.content || "",
      isMaster: templateDataById?.isMaster ?? false,
      isTenant: templateDataById?.isTenant ?? false,
      isRequired: templateDataById?.isRequired ?? false,
      isActive: templateDataById?.isActive ?? true,
      
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setButtonDisable(true);
      setLoading(true);
      
      try {
        const payload = {
          templateTitle: values.templateTitle,
          templateContent: values.templateContent,
          isMaster: values.isMaster,
          isTenant: values.isTenant,
          isActive: values.isActive,
          isRequired: values.isRequired
        };
        if (templateDataById) {
          const res = await updateTermsAndConditionsAction(payload, templateDataById.id);
          setSnackbar({
            open: true,
            message: res.message || "Terms & Conditions updated successfully",
            severity: "success",
          });
        } else {
          const res = await createTermsAndConditionsAction(payload);
          setSnackbar({
            open: true,
            message: res.message || "Terms & Conditions created successfully",
            severity: "success",
          });
        }
        
        formik.resetForm();
        handleClose();
        getTemplateData();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || "Something went wrong!",
          severity: "error",
        });
      } finally {
        setLoading(false);
        setButtonDisable(false);
      }
    },
  });

  const handleCheckboxChange = (e) => {
    formik.handleChange(e);
    formik.setFieldTouched(e.target.name, true, false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle className="fw-semibold fs-2 border-bottom">
        {templateDataById ? "Update Terms & Conditions" : "Add Terms & Conditions"}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 15,
        }}
      >
        <MyCloseIcon />
      </IconButton>
      
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          {/* Title Field */}
          <Box >
            <label className="form-label">Title <span className="text-danger">*</span></label>
            <OutlinedInput
              fullWidth
              size="small"
              name="templateTitle"
              placeholder="Enter template title"
              value={formik.values.templateTitle}
              onChange={(e) => {
                formik.setFieldValue("templateTitle", e.target.value.replace(/^\s+/, ''));
                formik.setFieldTouched("templateTitle", true, false);
              }}
              onBlur={(e) => {
                formik.setFieldValue("templateTitle", e.target.value.trim());
                formik.handleBlur(e);
              }}
              error={formik.touched.templateTitle && Boolean(formik.errors.templateTitle)}
              sx={SelectDropdown_SX}
            />
            {formik.touched.templateTitle && formik.errors.templateTitle && (
              <FormHelperText error>
                {formik.errors.templateTitle}
              </FormHelperText>
            )}
          </Box>


          <Divider sx={{ my: 2 }} />

          {/* Template Content */}
          <Box  sx={{ mb: 3 }}>
            <label className="form-label">Template Content <span className="text-danger">*</span></label>
            <Box
              sx={{
                border: formik.touched.templateContent && formik.errors.templateContent 
                  ? "1px solid #d32f2f" 
                  : "1px solid #ccc",
                borderRadius: 1,
              }}
            >
              <TipTapEditorComponent
                key={templateDataById?.id || 'new'}
                initialValue={formik.values.templateContent}
                disablePlugins={["media", "image"]}
                handleChange={(content) => {
                  formik.setFieldValue("templateContent", content); 
                  formik.setFieldTouched("templateContent", true, false); 
                }}
                handleBlur={(content) => {
                  const trimmedContent = content?.trim() || "";
                  formik.setFieldValue("templateContent", trimmedContent);
                  formik.setFieldTouched("templateContent", true);
                }}
                isDisabled={loading}
                // error={formik.touched.templateContent && formik.errors.templateContent}
              />
            </Box>
            {formik.touched.templateContent && formik.errors.templateContent && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: 'block' }}
              >
                {formik.errors.templateContent}
              </Typography>
            )}
          </Box>

          
          {/* Template Type Selection */}
          <Box sx={{ mb: 3 }}>
            <label className="form-label">Template Type <span className="text-danger">*</span></label>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isMaster"
                    checked={formik.values.isMaster}
                    onChange={handleCheckboxChange}
                    onBlur={formik.handleBlur}
                    color="primary"
                  />
                }
                label="Master"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="isTenant"
                    checked={formik.values.isTenant}
                    onChange={handleCheckboxChange}
                    onBlur={formik.handleBlur}
                    color="secondary"
                  />
                }
                label="Tenant"
              />
            </FormGroup>
            {formik.errors.isMaster && (
              <FormHelperText error>
                {formik.errors.isMaster}
              </FormHelperText>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 4,
                flexWrap: 'wrap'
              }}
            >

              {/* Is Required Toggle */}
              <Box sx={{ minWidth: 220 }}>
                <label className="form-label">Is Required</label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isRequired"
                        checked={formik.values.isRequired}
                        onChange={formik.handleChange}
                        color="primary"
                      />
                    }
                    label={
                      <Typography
                        component="span"
                        sx={{
                          color: formik.values.isRequired ? 'primary.main' : 'text.secondary',
                          fontWeight: 500
                        }}
                      >
                        {formik.values.isRequired ? "Enable" : "Disable"}
                      </Typography>
                    }
                  />
                </Box>
              </Box>
              
              {/* Status Toggle */}
              <Box sx={{ minWidth: 220 }}>
                <label className="form-label">Status</label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={formik.values.isActive}
                        onChange={formik.handleChange}
                        color="success"
                      />
                    }
                    label={
                      <Typography
                        component="span"
                        sx={{
                          color: formik.values.isActive ? 'success.main' : 'error.main',
                          fontWeight: 500
                        }}
                      >
                        {formik.values.isActive ? "Active" : "Inactive"}
                      </Typography>
                    }
                  />
                </Box>
              </Box>

            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions className="px-7 py-5 d-flex justify-content-between border-top">
          <button
            type="button"
            className="btn btn-outline btn-outline-primary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <div>
            {templateDataById && (
              <button
                type="button"
                className="btn btn-outline btn-outline-primary me-3"
                onClick={handlePreviewModel}
                disabled={loading}
              >
                Preview Template
              </button>
            )}
            <button
              className="btn btn-primary"
              type="submit"
              disabled={!formik.isValid || buttonDisable || loading}
            >
              {templateDataById ? "Update" : "Create"} Terms & Conditions
              {loading && (
                <span
                  className="spinner-border spinner-border-sm ms-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
        </DialogActions>
      </form>
      
      {openPreviewModel && (
        <PreviewTemplateModel
          open={openPreviewModel}
          handleClose={handleCloseModal}
          contentData={{
            ...templateDataById,
            title: formik.values.templateTitle,
            content: formik.values.templateContent,
          }}
        />
      )}
    </Dialog>
  );
};

export default AddEditTermsConditionModal;